import moment from "moment";
import {
  CodeAndDescription,
  CreditReportAccountOverview
} from "types/landlord/reports";

const NO_DATA_CELL: CodeAndDescription = {
  code: "no_data",
  description: "No payment history"
};

const reportedMonth = (date_reported?: string | null) => {
  const parsed = date_reported ? moment(date_reported) : null;

  return parsed?.isValid() ? parsed.month() : null;
};

const leadingPadding = (date_reported?: string | null) => {
  const month = reportedMonth(date_reported);

  return month === null ? 0 : 11 - month;
};

const filterHistory = (
  payment_history: CreditReportAccountOverview["payment_history"]
) => payment_history?.filter((el) => el.code !== "/") || [];

export const buildPaymentHistoryGrid = (
  payment_history: CreditReportAccountOverview["payment_history"],
  date_reported: string
): CodeAndDescription[] => {
  const filtered_payment_history = filterHistory(payment_history);

  if (!filtered_payment_history.length) return [];

  let ret = filtered_payment_history;

  if (reportedMonth(date_reported) !== null) {
    const head = leadingPadding(date_reported);
    const tail = (12 - ((head + filtered_payment_history.length) % 12)) % 12;

    ret = [
      ...Array(head).fill(NO_DATA_CELL),
      ...filtered_payment_history,
      ...Array(tail).fill(NO_DATA_CELL)
    ];
  }

  const inversed: CodeAndDescription[] = [];

  for (let i = 0; i < ret.length; i = i + 12)
    inversed.push(...ret.slice(i, i + 12).reverse());

  return inversed;
};

export const paymentHistoryGridIndex = (
  payment_history: CreditReportAccountOverview["payment_history"],
  date_reported: string | null | undefined,
  historyIndex: number
) => {
  const position = leadingPadding(date_reported) + historyIndex;
  const row = Math.floor(position / 12);

  if (reportedMonth(date_reported) !== null)
    return row * 12 + (11 - (position % 12));

  const rowLength = Math.min(
    12,
    filterHistory(payment_history).length - row * 12
  );

  return row * 12 + (rowLength - 1 - (position % 12));
};

export const paymentHistoryYears = (
  payment_history: CreditReportAccountOverview["payment_history"],
  date_reported?: string | null
): string[] => {
  const rowCount = Math.ceil(
    buildPaymentHistoryGrid(payment_history, date_reported || "").length / 12
  );

  if (!rowCount) return [];

  const anchor = date_reported ? moment(date_reported) : null;

  if (!anchor?.isValid()) return Array(rowCount).fill("");

  return Array.from({ length: rowCount }, (_, idx) =>
    anchor.clone().subtract(idx, "year").format("YYYY")
  );
};
